import { ViewShift } from '@xeetr/types/models'
import { weekdaysWithHoliydaySelections } from '@xeetr/types/utils'
import { format } from 'date-fns'
import { Copy, Ellipsis, Trash2 } from 'lucide-react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'

import FloatingLabel from '../../inputs/FloatingLabel'
import LabeledInputSwitch from '../../inputs/LabeledInputSwitch'
import TimePicker from '../common/TimePicker'

import Deadline from './deadline/Deadline'

import { Button } from '@/components/ui/button'
import { DateRangePicker } from '@/components/ui/date-range-picker'
import { Input } from '@/components/ui/input'
import { MultiSelect } from '@/components/ui/multi-select'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { showToast } from '@/utils/toastMessage'
import { useRooms } from '@/hooks'

interface Props {
  onCopy: () => void
  onDelete: () => void
}

const ShiftContent = ({ onCopy, onDelete }: Props) => {
  const { control, setValue } = useFormContext<ViewShift>()
  const { rooms } = useRooms()
  const deadlineOnlineReservation = useWatch({
    control,
    name: 'settings.deadlineOnlineReservation',
  })
  const isTimeFrameOptional = useWatch({
    control,
    name: 'isTimeFrameOptional',
  })
  const isHoliday = useWatch({
    control,
    name: 'isHoliday',
  })

  const handleAddTimeslots = (date: Date, value: string[], onChange: (value: string[]) => void) => {
    const time = format(date, 'HH:mm')
    if (value.includes(time)) {
      showToast({ summary: 'Warnung', severity: 'warn', detail: 'Zeit wurde bereits hinterlegt!' })
      return
    }
    const sortedTimeslots = [time, ...value].sort()
    onChange(sortedTimeslots)
  }

  const handleRemoveTimeslots = (
    time: string,
    values: string[],
    onChange: (value: string[]) => void
  ) => {
    const filteredTimeslots = values.filter((v) => v !== time)
    onChange(filteredTimeslots)
  }

  return (
    <div className="relative space-y-8">
      {/* Actions */}
      <div className="absolute right-0 top-0">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="icon">
              <Ellipsis className="h-5 w-5" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-56" align="end" side="bottom" sideOffset={5}>
            <div className="space-y-1">
              <div className="px-2 py-1.5 text-sm font-semibold">Optionen</div>
              <Separator />
              <Controller
                name="active"
                control={control}
                render={({ field }) => (
                  <div className="flex items-center justify-between rounded-sm px-2 py-2 hover:bg-gray-100">
                    <span className="text-sm">Aktiv</span>
                    <Switch checked={Boolean(field.value)} onCheckedChange={field.onChange} />
                  </div>
                )}
              />
              <Separator />
              <button
                className="flex w-full items-center gap-2 rounded-sm px-2 py-2 text-sm hover:bg-gray-100"
                onClick={() => onCopy()}
              >
                <Copy className="h-4 w-4" />
                Duplizieren
              </button>
              <button
                className="flex w-full items-center gap-2 rounded-sm px-2 py-2 text-sm text-red-600 hover:bg-red-50"
                onClick={() => onDelete()}
              >
                <Trash2 className="h-4 w-4" />
                Löschen
              </button>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Basic Information */}
      <section>
        <h3 className="mb-1 text-base font-semibold text-gray-900">Grundinformationen</h3>
        <p className="mb-4 text-sm text-gray-600">Name der Schicht für die interne Verwaltung</p>
        <Controller
          control={control}
          name="name"
          render={({ field }) => (
            <FloatingLabel label="Name" className="md:w-1/2">
              <Input {...field} value={field.value || ''} />
            </FloatingLabel>
          )}
        />
      </section>

      {/* Time Period */}
      <section>
        <h3 className="mb-1 text-base font-semibold text-gray-900">Zeitraum</h3>
        <p className="mb-4 text-sm text-gray-600">
          Definieren Sie den Gültigkeitszeitraum der Schicht
        </p>
        <div className="space-y-4">
          <Controller
            name="isTimeFrameOptional"
            control={control}
            render={({ field }) => (
              <LabeledInputSwitch
                ref={field.ref}
                label="Zeitraum für Schicht optional"
                checked={Boolean(field.value)}
                onValueChange={(e) => {
                  field.onChange(e)
                  if (e)
                    setValue('isHoliday', false, {
                      shouldValidate: true,
                      shouldDirty: true,
                      shouldTouch: true,
                    })
                }}
              />
            )}
          />
          {!isTimeFrameOptional && (
            <Controller
              control={control}
              name="from"
              render={({ field: fromField }) => (
                <Controller
                  control={control}
                  name="until"
                  render={({ field: untilField }) => (
                    <FloatingLabel label="Zeitraum" className="md:w-1/2">
                      <DateRangePicker
                        from={fromField.value as Date | undefined}
                        to={untilField.value as Date | undefined}
                        onDateRangeChange={(range) => {
                          if (range.from) fromField.onChange(range.from)
                          if (range.to) untilField.onChange(range.to)
                        }}
                        showIcon
                        dateFormat="dd.MM.yy"
                      />
                    </FloatingLabel>
                  )}
                />
              )}
            />
          )}
        </div>
      </section>

      {/* Operating Days */}
      <section>
        <h3 className="mb-1 text-base font-semibold text-gray-900">Betriebstage</h3>
        <p className="mb-4 text-sm text-gray-600">Wochentage oder Betriebsferien</p>
        <div className="space-y-4">
          <Controller
            name="isHoliday"
            control={control}
            render={({ field }) => (
              <LabeledInputSwitch
                ref={field.ref}
                label="Betriebsferien"
                disabled={isTimeFrameOptional}
                checked={Boolean(field.value)}
                onValueChange={field.onChange}
              />
            )}
          />
          {!isHoliday && (
            <Controller
              name="weekdays"
              control={control}
              render={({ field, fieldState }) => (
                <FloatingLabel
                  label={`Wochentag${(field.value?.length || 0) > 1 ? 'e' : ''}`}
                  className="md:w-1/2"
                >
                  <MultiSelect
                    value={field.value?.map(String)}
                    onValueChange={(value) => field.onChange(value.map(Number))}
                    className={cn({
                      'p-invalid': fieldState.error,
                    })}
                    options={weekdaysWithHoliydaySelections.map((w) => ({
                      value: String(w.value),
                      label: w.weekday,
                    }))}
                    placeholder="Wähle Wochentage"
                  />
                </FloatingLabel>
              )}
            />
          )}
        </div>
      </section>

      {/* Rooms */}
      <section>
        <h3 className="mb-1 text-base font-semibold text-gray-900">Räume</h3>
        <p className="mb-4 text-sm text-gray-600">
          Wählen Sie die Räume aus, die für diese Schicht verfügbar sind
        </p>
        <Controller
          name="rooms"
          control={control}
          render={({ field, fieldState }) => (
            <MultiSelect
              value={field.value?.map((room) => room.id)}
              onValueChange={(value) => {
                const selectedRooms = rooms.filter((room) => value.includes(room.id))
                field.onChange(selectedRooms)
              }}
              className={cn('md:w-1/2', {
                'p-invalid': fieldState.error,
              })}
              options={rooms.map((room) => ({ value: room.id, label: room.name }))}
              placeholder="Wähle Räume"
            />
          )}
        />
      </section>

      {/* Time Slots */}
      <section>
        <h3 className="mb-1 text-base font-semibold text-gray-900">Zeitfenster</h3>
        <p className="mb-4 text-sm text-gray-600">Verfügbare Buchungszeiten für diese Schicht</p>
        <Controller
          control={control}
          name="timeslots"
          render={({ field }) => (
            <TimePicker
              values={field.value ?? []}
              onChange={(date) => handleAddTimeslots(date, field.value ?? [], field.onChange)}
              onRemove={(time) => handleRemoveTimeslots(time, field.value ?? [], field.onChange)}
            />
          )}
        />
      </section>

      {/* Booking Deadline */}
      <section>
        <h3 className="mb-1 text-base font-semibold text-gray-900">Buchungsfrist</h3>
        <div className="space-y-4">
          <Controller
            control={control}
            name="settings.deadlineOnlineReservation"
            render={({ field }) => (
              <FloatingLabel label="Deadline für die Online-Reservierung" className="md:w-1/2">
                <Deadline value={field.value} onChange={field.onChange} />
              </FloatingLabel>
            )}
          />
          {deadlineOnlineReservation && (
            <Controller
              control={control}
              name="settings.deadlineOnlineReservationNote"
              render={({ field }) => (
                <FloatingLabel
                  label="Hinweis zur Deadline für Online Reservierungen"
                  tooltip={`Dieser Text wird deinem Kunden bei der Reservierung angezeigt, sollte es später als ${format(
                    deadlineOnlineReservation,
                    'HH:mm'
                  )} sein.`}
                >
                  <Textarea {...field} value={field.value || ''} rows={5} />
                </FloatingLabel>
              )}
            />
          )}
        </div>
      </section>

      {/* Notes */}
      <section>
        <h3 className="mb-1 text-base font-semibold text-gray-900">Notizen</h3>
        <Controller
          control={control}
          name="settings.note"
          render={({ field }) => (
            <FloatingLabel label="Interne Notizen zu dieser Schicht">
              <Textarea {...field} value={field.value || ''} rows={5} />
            </FloatingLabel>
          )}
        />
      </section>
    </div>
  )
}

export default ShiftContent
